import { WidgetProps } from '@highimpact-front/remote-widget-core';
import { GenericErrorBoundary } from '@highimpact/dashboard-ui';
import { PermissionDecorator } from '@highimpact/react-decorators';
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import { useImageGalleryResourceIds } from '../../../../../hooks/useImageGalleryResourceIds';
import { useResourcesQuery } from '../../../../../query/useResourcesQuery';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../../../../../runtime/DashboardLibraryProvider';
import {
  setDeleteWidgetConfirmation,
  setImageGalleryWidgetConfig,
} from '../../../../../state/dashboard/slice';
import NameHandlerComponent from '../../../../NameHandlerComponent';
import Widget from '../../../commons/Widget';
import { WidgetToolbar } from '../../../commons/WidgetToolbar';
import ImageGalleryDisplayer, {
  ApiReturnImageResource,
} from './ImageGalleryDisplayer';
import { useImageGalleryInstanceRegistration } from './useImageGalleryInstanceRegistration';
import { WidgetDragHandle } from '../../../commons/WidgetDragHandle';

export interface ImageGalleryProps extends WidgetProps {
  project_id: number;
  site_id: number;
  resource_id?: number;
  image: ApiReturnImageResource;
  tags: string[];
  gridId: number;
  grid_type: 'reports' | 'grids';
  non_removable?: boolean;
}

const ImageGallery = forwardRef<HTMLDivElement, ImageGalleryProps>(
  (
    {
      project_id,
      site_id,
      image: _image,
      resource_id: _resource_id,
      name,
      dashboardElementId,
      dashboardOnlyMode,
      grid_type,
      gridId,
      non_removable,
      tags,
      ...props
    },
    ref
  ) => {
    const dispatch = useDashboardLibraryDispatch();
    const galleryConfig = useDashboardLibrarySelector(
      (state) => state.dashboard.imageGallerySelectorConfig
    );
    const configWasOpenForThisWidget = useRef(false);
    const {
      resourceIds,
      loading: resourceIdsLoading,
      error: resourceIdsError,
      reload: reloadGalleryResources,
    } = useImageGalleryResourceIds(project_id, tags);
    const {
      resources,
      loading: resourcesLoading,
      reload: reloadGalleryImages,
    } = useResourcesQuery(resourceIds);
    const galleryImages = useMemo(
      () => resources as ApiReturnImageResource[],
      [resources]
    );

    useImageGalleryInstanceRegistration({
      dashboardElementId,
      widgetId: props.widget_id,
      enabled:
        !resourceIdsLoading && !resourcesLoading && galleryImages.length > 0,
    });

    useEffect(() => {
      const openForThisWidget =
        galleryConfig.state === 'open' &&
        galleryConfig.widgetId === props.widget_id &&
        galleryConfig.dashboardId === gridId &&
        galleryConfig.grid_type === grid_type;

      if (openForThisWidget) {
        configWasOpenForThisWidget.current = true;
        return;
      }

      if (configWasOpenForThisWidget.current) {
        configWasOpenForThisWidget.current = false;
        void reloadGalleryResources().then(() => {
          void reloadGalleryImages();
        });
      }
    }, [
      galleryConfig,
      gridId,
      grid_type,
      props.widget_id,
      reloadGalleryImages,
      reloadGalleryResources,
    ]);

    const openConfig = () => {
      dispatch(
        setImageGalleryWidgetConfig({
          dashboardId: gridId,
          widgetId: props.widget_id,
          state: 'open',
          projectId: project_id,
          siteId: site_id,
          tags: tags,
          grid_type: grid_type,
        })
      );
    };

    return (
      <Widget
        ref={ref}
        {...props}
        className={`image-widget-root ${props.className ?? ''}`.trim()}
        dashboardElementId={dashboardElementId}
      >
        <GenericErrorBoundary>
          <div className="image-widget">
            <PermissionDecorator
              item="dashboard_nav_bar_widget"
              project_id={
                Number.isNaN(project_id) ? undefined : Number(project_id)
              }
              site_id={Number.isNaN(site_id) ? undefined : Number(site_id)}
              extra={{ dashboard_id: gridId }}
            >
              <div className="widget-nav">
                <p style={{ display: 'flex' }}>
                  {!dashboardOnlyMode && (
                    <PermissionDecorator item="dashboard_move_widget">
                      <WidgetDragHandle />
                    </PermissionDecorator>
                  )}
                  <PermissionDecorator item="dashboard_rename_widget">
                    <NameHandlerComponent
                      widgetId={dashboardElementId}
                      dashboardElementId={props.widget_id}
                      dashboardId={gridId}
                      dashboardType={grid_type}
                    />
                  </PermissionDecorator>
                </p>
                {!dashboardOnlyMode && (
                  <div className="nav-icon">
                    <WidgetToolbar
                      dashboardElementId={dashboardElementId}
                      dashboardId={gridId}
                      // TODO: replace
                      platformActions={{
                        edit: {
                          visible: true,
                          tooltipText: "Configurer l'élément",
                          permissionItem: 'dashboard_configure_widget',
                          onClick: openConfig,
                        },
                        delete: !non_removable
                          ? {
                              visible: true,
                              tooltipText: "Supprimer l'élément",
                              permissionItem: 'dashboard_delete_widget',
                              onClick: () =>
                                dispatch(
                                  setDeleteWidgetConfirmation({
                                    state: 'open',
                                    dashboardElementId,
                                    widgetName: name ?? 'de nom inconnu',
                                  })
                                ),
                            }
                          : undefined,
                      }}
                    />
                  </div>
                )}
              </div>
            </PermissionDecorator>
            {tags.length === 0 ? null : resourceIdsLoading ||
              resourcesLoading ? (
              <p>Chargement de la galerie...</p>
            ) : resourceIdsError ? (
              <p>Impossible de charger les images de la galerie.</p>
            ) : galleryImages.length > 0 ? (
              <ImageGalleryDisplayer images={galleryImages} />
            ) : null}
          </div>
        </GenericErrorBoundary>
      </Widget>
    );
  }
);

export default ImageGallery;
